import { z } from 'zod';
import { PaymentMethod } from '@/data/catalog/offer/interfaces';
import { registrationSchema } from '@/packages/courses/components/user-data-form/schema';
import { CheckoutStepIds } from '@/packages/courses/pages/checkout/types';

const requiredMessage = 'Campo obrigatório';

export const paymentMethodSchema = z.object({
  paymentMethod: z.enum(PaymentMethod, {
    message: 'Selecione uma forma de pagamento para continuar.',
  }),
});

export const creditCardSchema = z.object({
  number: z
    .string({ message: requiredMessage })
    .min(19, 'Número do cartão inválido')
    .refine(
      (value) => {
        const cleanedNumber = value.replace(/\s/g, '');
        return cleanedNumber.length === 16 && /^\d+$/.test(cleanedNumber);
      },
      { message: 'Número do cartão deve ter 16 dígitos' }
    ),
  holder_name: z
    .string({ message: requiredMessage })
    .min(3, 'Nome deve ter no mínimo 3 caracteres'),
  expiration: z
    .string({ message: requiredMessage })
    .length(5, 'Vencimento deve estar no formato MM/AA')
    .refine(
      (value) => {
        const [month, year] = value.split('/');
        if (!month || !year) return false;

        const monthNum = parseInt(month);
        const yearNum = parseInt(year);
        const currentYear = new Date().getFullYear() % 100;

        return (
          month.length === 2 &&
          year.length === 2 &&
          monthNum >= 1 &&
          monthNum <= 12 &&
          yearNum >= currentYear
        );
      },
      { message: 'Vencimento inválido' }
    ),
  cvv: z
    .string({ message: requiredMessage })
    .min(3, 'CVV deve ter 3 ou 4 dígitos')
    .max(4, 'CVV deve ter 3 ou 4 dígitos')
    .regex(/^\d+$/, 'CVV deve conter apenas números'),
  holder_document: z
    .string({ message: requiredMessage })
    .length(14, 'CPF inválido')
    .refine(
      (value) => {
        const cpf = value.replace(/\D/g, '');
        return cpf.length === 11;
      },
      { message: 'CPF deve ter 11 dígitos' }
    ),
});

export const installmentsSchema = z.object({
  installments: z.number({ message: 'Selecione o número de parcelas' }),
});

export const emptySchema = z.object({});

export const schemasByStep = {
  [CheckoutStepIds.CustomerInfo]: registrationSchema,
  [CheckoutStepIds.PaymentMethod]: paymentMethodSchema,
  [CheckoutStepIds.CreditCardDetails]: creditCardSchema,
  [CheckoutStepIds.CreditCardInstallments]: installmentsSchema,
  [CheckoutStepIds.DataConfirmation]: emptySchema,
  [CheckoutStepIds.PixBankSlip]: emptySchema,
  [CheckoutStepIds.PaymentStatus]: emptySchema,
};

export type PaymentMethodFormData = z.infer<typeof paymentMethodSchema>;
export type CreditCardFormData = z.infer<typeof creditCardSchema>;
export type InstallmentsFormData = z.infer<typeof installmentsSchema>;
